/**
 * player-search.ts - Player Database Search Functionality
 *
 * Handles searching FIDE and Swiss Chess databases for players,
 * with tabs in the dropdown and optional club filtering.
 */

import { API_BASE_URL, PLAYER_DATABASES } from '../../utils/constants';
import { parseFideResults, parseSwissChessResults, type PlayerSearchResult } from './player-search-parsers';
import { selectPlayer } from './player-suggestions';
import { escapeHtml } from '../../utils/security';

// Track which tournaments have already scrolled to avoid repeated scrolling
const scrolledTournaments = new Set<string>();

function resetSuggestionsScroll(tournamentId: string): void {
  scrolledTournaments.delete(tournamentId);
}

// Player Search functionality
let playerSearchTimeout: number | null = null;
const playerSearchCache = new Map<string, PlayerSearchResult[]>();

// State management for tabs and club filter per tournament
const activeTabState = new Map<string, string>();
const clubFilterState = new Map<string, string>();

/**
 * Get translation helper
 */
function t(key: string, fallback: string): string {
  return window.i18n?.t(key) ?? fallback;
}

/**
 * Get the tournament's time control setting
 */
function getTournamentTimeControl(tournamentId: string): string {
  const activeButton = document.querySelector(`#time-control-${tournamentId} .segment-button.active`) as HTMLElement | null;
  return activeButton?.dataset['value'] || 'classical';
}

/**
 * Get the appropriate rating based on tournament time control
 */
function getRatingForTimeControl(player: PlayerSearchResult, timeControl: string): number {
  switch (timeControl) {
    case 'blitz':
      return player.ratingBlitz || player.ratingRapid || player.ratingStd || player.rating || 0;
    case 'rapid':
      return player.ratingRapid || player.ratingStd || player.ratingBlitz || player.rating || 0;
    case 'classical':
    default:
      return player.ratingStd || player.ratingRapid || player.ratingBlitz || player.rating || 0;
  }
}

/**
 * Parse search term into firstName and lastName parts
 * If multiple words: first words = firstName, last word = lastName
 * If single word: matches either firstName or lastName
 */
function parseSearchTerm(term: string): { firstNamePart: string; lastNamePart: string; isSingleWord: boolean } {
  const parts = term.trim().split(/\s+/);
  if (parts.length === 1) {
    return { firstNamePart: parts[0], lastNamePart: parts[0], isSingleWord: true };
  }
  // Multiple words - last word is lastName, rest is firstName
  const lastNamePart = parts[parts.length - 1];
  const firstNamePart = parts.slice(0, -1).join(' ');
  return { firstNamePart, lastNamePart, isSingleWord: false };
}

/**
 * Merge two player result arrays, removing duplicates by fideId or federationCode
 */
function mergePlayerResults(list1: PlayerSearchResult[], list2: PlayerSearchResult[]): PlayerSearchResult[] {
  const seen = new Set<string>();
  const result: PlayerSearchResult[] = [];

  for (const player of [...list1, ...list2]) {
    // Create a unique key from available identifiers
    const key = player.fideId || player.federationCode || `${player.firstName}-${player.lastName}-${player.rating}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(player);
    }
  }

  return result;
}

/**
 * Filter players by full name (firstName + lastName)
 */
function filterByFullName(players: PlayerSearchResult[], searchTerm: string): PlayerSearchResult[] {
  const { firstNamePart, lastNamePart, isSingleWord } = parseSearchTerm(searchTerm);
  const firstLower = firstNamePart.toLowerCase();
  const lastLower = lastNamePart.toLowerCase();

  return players.filter(p => {
    const firstName = (p.firstName || '').toLowerCase();
    const lastName = (p.lastName || '').toLowerCase();

    if (isSingleWord) {
      // Single word - match either firstName or lastName
      return firstName.includes(firstLower) || lastName.includes(lastLower);
    } else {
      // Multiple words - firstName must match first part, lastName must match last part
      return firstName.includes(firstLower) && lastName.includes(lastLower);
    }
  });
}

/**
 * Setup player search functionality for a tournament
 * @param tournamentId - The tournament ID
 */
export function setupPlayerSearch(tournamentId: string): void {
  const searchInput = document.getElementById(`player-search-${tournamentId}`) as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);

  if (!searchInput || !suggestionsDiv) return;

  // Initialize state only if not already set (preserve between player additions)
  if (!activeTabState.has(tournamentId)) {
    activeTabState.set(tournamentId, PLAYER_DATABASES.FIDE);
  }
  if (!clubFilterState.has(tournamentId)) {
    clubFilterState.set(tournamentId, '');
  }

  // Handle search input
  searchInput.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    const searchTerm = target.value.trim();

    // Clear previous timeout
    if (playerSearchTimeout) {
      clearTimeout(playerSearchTimeout);
    }

    // Hide suggestions if search term is too short
    if (searchTerm.length < 3) {
      suggestionsDiv.style.display = 'none';
      resetSuggestionsScroll(tournamentId);
      return;
    }

    // Debounce the search
    playerSearchTimeout = window.setTimeout(async () => {
      await searchPlayers(searchTerm, tournamentId);
    }, 400);
  });

  // Close suggestions when clicking outside
  document.addEventListener('click', (e) => {
    const target = e.target as Node;
    if (!searchInput.contains(target) && !suggestionsDiv.contains(target)) {
      suggestionsDiv.style.display = 'none';
      resetSuggestionsScroll(tournamentId);
    }
  });

  // Handle keyboard
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      suggestionsDiv.style.display = 'none';
    }
  });
}

/**
 * Build dropdown header with tabs and club filter
 */
function buildDropdownHeader(tournamentId: string, activeTab: string, clubFilter: string): string {
  return `
    <div class="suggestions-header">
      <div class="suggestions-tabs">
        <button class="suggestions-tab ${activeTab === PLAYER_DATABASES.FIDE ? 'active' : ''}" data-tab="${PLAYER_DATABASES.FIDE}">${t('player.fideTab', 'FIDE')}</button>
        <button class="suggestions-tab ${activeTab === PLAYER_DATABASES.SWISS_CHESS ? 'active' : ''}" data-tab="${PLAYER_DATABASES.SWISS_CHESS}">${t('player.swissChessTab', 'Swiss Chess')}</button>
      </div>
      <div class="suggestions-club-filter ${activeTab === PLAYER_DATABASES.SWISS_CHESS ? '' : 'hidden'}">
        <input type="text"
          id="player-club-filter-${tournamentId}"
          placeholder="${t('player.filterByClub', 'Filter by club...')}"
          value="${escapeHtml(clubFilter)}"
          autocomplete="off">
      </div>
    </div>
  `;
}

/**
 * Setup event handlers for the dropdown header
 */
function setupHeaderHandlers(tournamentId: string, currentSearchTerm: string): void {
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);
  if (!suggestionsDiv) return;

  // Tab switching
  suggestionsDiv.querySelectorAll('.suggestions-tab').forEach(tab => {
    tab.addEventListener('click', (e) => {
      e.stopPropagation();
      const newTab = (tab as HTMLElement).dataset['tab'];
      if (newTab) {
        activeTabState.set(tournamentId, newTab);

        // Update tab active states visually
        suggestionsDiv.querySelectorAll('.suggestions-tab').forEach(t => {
          if ((t as HTMLElement).dataset['tab'] === newTab) {
            t.classList.add('active');
          } else {
            t.classList.remove('active');
          }
        });

        // Show/hide club filter based on tab
        const clubFilterDiv = suggestionsDiv.querySelector('.suggestions-club-filter');
        if (clubFilterDiv) {
          if (newTab === PLAYER_DATABASES.SWISS_CHESS) {
            clubFilterDiv.classList.remove('hidden');
          } else {
            clubFilterDiv.classList.add('hidden');
          }
        }

        // Re-search with new tab
        searchPlayers(currentSearchTerm, tournamentId);
      }
    });
  });

  // Club filter input
  const clubFilterInput = document.getElementById(`player-club-filter-${tournamentId}`) as HTMLInputElement | null;
  if (clubFilterInput) {
    let clubFilterTimeout: number | null = null;

    clubFilterInput.addEventListener('input', (e) => {
      e.stopPropagation();
      const target = e.target as HTMLInputElement;
      clubFilterState.set(tournamentId, target.value.trim());

      if (clubFilterTimeout) {
        clearTimeout(clubFilterTimeout);
      }

      clubFilterTimeout = window.setTimeout(() => {
        searchPlayers(currentSearchTerm, tournamentId);
      }, 400);
    });

    // Prevent closing dropdown when interacting with club filter
    clubFilterInput.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    clubFilterInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        suggestionsDiv.style.display = 'none';
      }
    });
  }
}

/**
 * Position the suggestions dropdown below the search input
 */
function positionSuggestionsDropdown(tournamentId: string): void {
  const searchInput = document.getElementById(`player-search-${tournamentId}`);
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);

  if (!searchInput || !suggestionsDiv) return;

  const rect = searchInput.getBoundingClientRect();
  suggestionsDiv.style.top = `${rect.bottom + 4}px`;
  suggestionsDiv.style.left = `${rect.left}px`;
}

/**
 * Search for players in both databases
 */
async function searchPlayers(searchTerm: string, tournamentId: string): Promise<void> {
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);
  if (!suggestionsDiv) return;

  // Position the dropdown
  positionSuggestionsDropdown(tournamentId);

  const activeTab = activeTabState.get(tournamentId) || PLAYER_DATABASES.FIDE;
  const clubFilter = clubFilterState.get(tournamentId) || '';

  // Check if dropdown is already visible - if so, only update content
  const isAlreadyVisible = suggestionsDiv.style.display === 'block';
  let contentDiv = suggestionsDiv.querySelector('.suggestions-content');

  if (!isAlreadyVisible || !contentDiv) {
    // Build full dropdown HTML with header
    const headerHtml = buildDropdownHeader(tournamentId, activeTab, clubFilter);
    suggestionsDiv.innerHTML = headerHtml + '<div class="suggestions-content"><div class="player-loading">Searching...</div></div>';
    suggestionsDiv.style.display = 'block';
    // Setup header event handlers
    setupHeaderHandlers(tournamentId, searchTerm);
    contentDiv = suggestionsDiv.querySelector('.suggestions-content');
  } else {
    // Just update the content area
    contentDiv.innerHTML = '<div class="player-loading">Searching...</div>';
  }

  // Only scroll once when dropdown first appears
  if (!scrolledTournaments.has(tournamentId)) {
    scrolledTournaments.add(tournamentId);
    requestAnimationFrame(() => {
      suggestionsDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  try {
    // Parse search term
    const { firstNamePart, lastNamePart, isSingleWord } = parseSearchTerm(searchTerm);

    let fidePlayers: PlayerSearchResult[];
    let swissChessPlayers: PlayerSearchResult[];

    if (clubFilter) {
      // With club filter: search SwissChess by club, then filter by full name
      const [fideResults, clubResults] = await Promise.all([
        searchPlayerDatabase(lastNamePart, PLAYER_DATABASES.FIDE),
        searchSwissChessByClubOnly(clubFilter)
      ]);
      fidePlayers = filterByFullName(fideResults, searchTerm);
      swissChessPlayers = filterByFullName(clubResults, searchTerm);
    } else if (isSingleWord) {
      // Single word: search by that word
      const [fideResults, swissResults] = await Promise.all([
        searchPlayerDatabase(lastNamePart, PLAYER_DATABASES.FIDE),
        searchPlayerDatabase(lastNamePart, PLAYER_DATABASES.SWISS_CHESS)
      ]);
      fidePlayers = filterByFullName(fideResults, searchTerm);
      swissChessPlayers = filterByFullName(swissResults, searchTerm);
    } else {
      // Multiple words: search by both firstName and lastName parts, merge results
      // This helps when lastName part is too short for the API
      const [fideByLast, fideByFirst, swissByLast, swissByFirst] = await Promise.all([
        searchPlayerDatabase(lastNamePart, PLAYER_DATABASES.FIDE),
        searchPlayerDatabase(firstNamePart, PLAYER_DATABASES.FIDE),
        searchPlayerDatabase(lastNamePart, PLAYER_DATABASES.SWISS_CHESS),
        searchPlayerDatabase(firstNamePart, PLAYER_DATABASES.SWISS_CHESS)
      ]);
      // Merge and dedupe by fideId or federationCode
      const fideAll = mergePlayerResults(fideByLast, fideByFirst);
      const swissAll = mergePlayerResults(swissByLast, swissByFirst);
      fidePlayers = filterByFullName(fideAll, searchTerm);
      swissChessPlayers = filterByFullName(swissAll, searchTerm);
    }

    displaySuggestions(fidePlayers, swissChessPlayers, tournamentId, activeTab, searchTerm);
  } catch (error) {
    console.error('Error fetching player data:', error);
    if (contentDiv) {
      contentDiv.innerHTML = '<div class="player-error">Error searching database</div>';
    }
  }
}

/**
 * Search a player database
 */
async function searchPlayerDatabase(searchTerm: string, database: string): Promise<PlayerSearchResult[]> {
  const cacheKey = `${database}:${searchTerm}`;

  if (playerSearchCache.has(cacheKey)) {
    return playerSearchCache.get(cacheKey)!;
  }

  try {
    const endpoint = database === PLAYER_DATABASES.FIDE ? 'fide-search' : 'swisschess-search';
    const response = await fetch(`${API_BASE_URL}/${endpoint}?query=${encodeURIComponent(searchTerm)}`, {
      credentials: 'include',
    });
    const html = await response.text();
    const players = database === PLAYER_DATABASES.FIDE ? parseFideResults(html) : parseSwissChessResults(html);

    playerSearchCache.set(cacheKey, players);
    return players;
  } catch (error) {
    console.error(`Error fetching ${database} player data:`, error);
    return [];
  }
}

/**
 * Search SwissChess database by club only (returns all players from that club)
 */
async function searchSwissChessByClubOnly(clubName: string): Promise<PlayerSearchResult[]> {
  const cacheKey = `swisschess:clubonly:${clubName}`;

  if (playerSearchCache.has(cacheKey)) {
    return playerSearchCache.get(cacheKey)!;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/swisschess-search?club=${encodeURIComponent(clubName)}`,
      { credentials: 'include' }
    );
    const html = await response.text();
    const players = parseSwissChessResults(html);

    playerSearchCache.set(cacheKey, players);
    return players;
  } catch (error) {
    console.error('Error fetching SwissChess players by club:', error);
    return [];
  }
}

/**
 * Display suggestions with the currently active tab
 */
function displaySuggestions(
  fidePlayers: PlayerSearchResult[],
  swissChessPlayers: PlayerSearchResult[],
  tournamentId: string,
  activeTab: string,
  searchTerm: string
): void {
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);
  if (!suggestionsDiv) return;

  const players = activeTab === PLAYER_DATABASES.FIDE ? fidePlayers : swissChessPlayers;
  const showFideId = activeTab === PLAYER_DATABASES.FIDE;
  const clubFilter = clubFilterState.get(tournamentId) || '';
  const { isSingleWord } = parseSearchTerm(searchTerm);

  let contentHtml: string;
  if (players.length === 0) {
    // Provide helpful message based on search context
    let noResultsMsg: string;
    if (activeTab === PLAYER_DATABASES.SWISS_CHESS && clubFilter) {
      noResultsMsg = t('player.noPlayersInClub', 'No players found in this club');
    } else if (isSingleWord && !clubFilter) {
      // Single word without club filter - might be searching by firstName
      noResultsMsg = t('player.tryClubFilter', 'No players found. Try adding a club filter for first name search.');
    } else {
      noResultsMsg = t('player.noPlayersFound', 'No players found');
    }
    contentHtml = `<div class="player-no-results">${noResultsMsg}</div>`;
  } else {
    contentHtml = players.map(p => renderPlayerItem(p, tournamentId, showFideId)).join('');
  }

  // Only update content area, not the header (to preserve focus on club filter)
  const contentDiv = suggestionsDiv.querySelector('.suggestions-content');
  if (contentDiv) {
    contentDiv.innerHTML = contentHtml;
  }

  // Add click handlers for player items
  suggestionsDiv.querySelectorAll('.player-suggestion-item').forEach(item => {
    item.addEventListener('click', () => {
      const playerData = (item as HTMLElement).dataset['player'];
      if (playerData) {
        const player = JSON.parse(playerData) as PlayerSearchResult;
        const timeControl = getTournamentTimeControl(tournamentId);
        const rating = getRatingForTimeControl(player, timeControl);
        selectPlayer(
          tournamentId,
          player.firstName,
          player.lastName,
          rating,
          player.title || null,
          player.club || null,
          player.federation || null,
          player.fideId || null,
          player.federationCode || null
        );
      }
    });
  });
}

/**
 * Render a single player item HTML
 */
function renderPlayerItem(player: PlayerSearchResult, tournamentId: string, showFideId: boolean): string {
  const displayName = `${escapeHtml(player.firstName)} ${escapeHtml(player.lastName)}`.trim();
  const timeControl = getTournamentTimeControl(tournamentId);
  const displayRating = getRatingForTimeControl(player, timeControl);

  return `
    <div class="player-suggestion-item" data-player='${escapeHtml(JSON.stringify(player))}'>
      <div class="player-suggestion-name">${player.title ? escapeHtml(player.title) + ' ' : ''}${displayName}</div>
      <div class="player-suggestion-details">
        <span class="player-rating">${displayRating}</span>
        <span class="player-federation">${player.federation ? escapeHtml(player.federation) : ''}</span>
        ${player.club ? `<span class="player-club">${escapeHtml(player.club)}</span>` : ''}
        ${showFideId
          ? (player.fideId ? `<span class="player-id">FIDE: ${player.fideId}</span>` : '')
          : (player.federationCode ? `<span class="player-id">Code: ${player.federationCode}</span>` : '')
        }
      </div>
    </div>
  `;
}

/**
 * Switch between FIDE and Swiss Chess suggestions tabs (legacy - kept for compatibility)
 */
export function switchSuggestionsTab(tournamentId: string, tabName: string): void {
  activeTabState.set(tournamentId, tabName);
}

// Export to window for global access
window.setupPlayerSearch = setupPlayerSearch;
window.switchSuggestionsTab = switchSuggestionsTab;
window.resetSuggestionsScroll = resetSuggestionsScroll;
